import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../shared/components/Layout';
import Pagination from '../../shared/components/Pagination';
import UserDetailModal from '../../shared/components/UserDetailModal';
import { extractErrorMessage } from '../../shared/utils/errors';
import {
  FinanceCategory,
  FinanceCategorySummary,
  FinanceOperation,
  FinancePlatformSummary,
  PaginatedResponse,
  PlatformModuleKey,
} from '../../shared/types';
import { formatUserLabel, useUserNames } from '../../shared/hooks/useUserNames';
import {
  OperationFilters,
  getOperationsSummary,
  getPlatformSummary,
  listOperations,
} from './api';
import OperationDetailModal from './OperationDetailModal';
import PlatformSummaryCards from './PlatformSummaryCards';
import {
  CURRENCY_OPTIONS,
  TYPE_LABELS,
  directionClass,
  directionPrefix,
  formatCryptoAmount,
  formatUsd,
} from './format';

interface ModuleOperationsPageProps {
  category: FinanceCategory;
  moduleKey: PlatformModuleKey;
  title: string;
  description: string;
}

type ModuleFilters = Omit<OperationFilters, 'category' | 'page' | 'per_page'>;

/**
 * Per-module money operations page: the module's ledger feed plus the
 * platform side of it (collected fees, platform balance and flows).
 */
const ModuleOperationsPage: React.FC<ModuleOperationsPageProps> = ({
  category,
  moduleKey,
  title,
  description,
}) => {
  const [operations, setOperations] =
    useState<PaginatedResponse<FinanceOperation> | null>(null);
  const [moduleSummary, setModuleSummary] = useState<FinanceCategorySummary | null>(null);
  const [platformSummary, setPlatformSummary] = useState<FinancePlatformSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState<ModuleFilters>({});
  const [draftFilters, setDraftFilters] = useState<ModuleFilters>({});

  const [profileUserId, setProfileUserId] = useState<number | null>(null);
  const [selectedOperation, setSelectedOperation] = useState<FinanceOperation | null>(null);

  const load = useCallback(
    async (page: number, filtersToUse: ModuleFilters) => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await listOperations({ ...filtersToUse, category, page, per_page: 20 });
        setOperations(data);
      } catch (err) {
        setError(extractErrorMessage(err, 'Failed to load operations'));
        setOperations(null);
      } finally {
        setIsLoading(false);
      }
    },
    [category]
  );

  const loadSummaries = useCallback(
    async (filtersToUse: ModuleFilters) => {
      setIsSummaryLoading(true);
      const [summaryResult, platformResult] = await Promise.allSettled([
        getOperationsSummary({ ...filtersToUse, category }),
        getPlatformSummary({
          currency: filtersToUse.currency,
          date_from: filtersToUse.date_from,
          date_to: filtersToUse.date_to,
        }),
      ]);
      setModuleSummary(
        summaryResult.status === 'fulfilled'
          ? (summaryResult.value.categories.find((c) => c.category === category) ?? null)
          : null
      );
      setPlatformSummary(platformResult.status === 'fulfilled' ? platformResult.value : null);
      setIsSummaryLoading(false);
    },
    [category]
  );

  useEffect(() => {
    load(currentPage, filters);
  }, [currentPage, filters, load]);

  useEffect(() => {
    loadSummaries(filters);
  }, [filters, loadSummaries]);

  const applyFilters = () => {
    setFilters(draftFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setDraftFilters({});
    setFilters({});
    setCurrentPage(1);
  };

  const userIds: number[] = [];
  if (operations?.data) {
    for (const op of operations.data) userIds.push(op.user_id);
  }
  const userNames = useUserNames(userIds);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-white">{title}</h1>
          <p className="text-slate-400 mt-1">{description}</p>
        </div>

        <PlatformSummaryCards
          summary={platformSummary}
          isLoading={isSummaryLoading}
          moduleKey={moduleKey}
        />

        {!isSummaryLoading && moduleSummary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="card p-4 border-indigo-500/30">
              <p className="text-xs uppercase tracking-wider text-slate-500">Module operations</p>
              <p className="text-2xl font-bold text-white mt-1">
                {moduleSummary.operations.toLocaleString('en-US')}
              </p>
              <div className="mt-2 space-y-0.5 text-xs font-mono">
                <p className="text-emerald-400">in: {formatUsd(moduleSummary.usd_in)}</p>
                <p className="text-rose-400">out: {formatUsd(moduleSummary.usd_out)}</p>
              </div>
            </div>
            <div className="card p-4">
              <p className="text-xs uppercase tracking-wider text-slate-500">Volumes by currency</p>
              <div className="mt-3 space-y-1 text-xs font-mono">
                {moduleSummary.currencies.map((c) => (
                  <div key={c.currency} className="flex justify-between gap-2">
                    <span className="text-slate-400">{c.currency}</span>
                    <span className="text-right">
                      {c.in > 0 && (
                        <span className="text-emerald-400">+{formatCryptoAmount(c.in)}</span>
                      )}
                      {c.in > 0 && c.out > 0 && <span className="text-slate-600"> / </span>}
                      {c.out > 0 && (
                        <span className="text-rose-400">−{formatCryptoAmount(c.out)}</span>
                      )}
                      {c.in === 0 && c.out === 0 && (
                        <span className="text-amber-400" title="Held / released holds">
                          {formatCryptoAmount(c.hold)}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
                {moduleSummary.currencies.length === 0 && (
                  <p className="text-slate-500">No volumes for the selected filters.</p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="card p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">User ID</label>
            <input
              type="number"
              min={1}
              value={draftFilters.user_id ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, user_id: e.target.value || undefined }))
              }
              className="input py-2"
              placeholder="e.g. 42"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Currency</label>
            <select
              value={draftFilters.currency ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, currency: e.target.value || undefined }))
              }
              className="input py-2"
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c || 'all-cur'} value={c}>
                  {c || 'Any'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">From</label>
            <input
              type="date"
              value={draftFilters.date_from ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, date_from: e.target.value || undefined }))
              }
              className="input py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">To</label>
            <input
              type="date"
              value={draftFilters.date_to ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, date_to: e.target.value || undefined }))
              }
              className="input py-2"
            />
          </div>
          <div className="flex gap-2 lg:col-span-2">
            <button type="button" onClick={applyFilters} className="btn btn-primary py-2 flex-1">
              Apply
            </button>
            <button type="button" onClick={clearFilters} className="btn btn-secondary py-2">
              Clear
            </button>
          </div>
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : !operations?.data.length ? (
            <div className="p-12 text-center text-slate-400">No operations found.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">Date</th>
                      <th className="table-header">User</th>
                      <th className="table-header">Operation</th>
                      <th className="table-header text-right">Amount</th>
                      <th className="table-header text-right">Balance after</th>
                      <th className="table-header">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {operations.data.map((op) => (
                      <tr
                        key={op.id}
                        className="hover:bg-slate-800/30 transition-colors cursor-pointer"
                        onClick={() => setSelectedOperation(op)}
                      >
                        <td className="table-cell text-slate-400 text-sm whitespace-nowrap">
                          {new Date(op.created_at).toLocaleString()}
                          <p className="text-xs text-slate-600 font-mono">#{op.id}</p>
                        </td>
                        <td className="table-cell whitespace-nowrap">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setProfileUserId(op.user_id);
                            }}
                            className="text-sky-400 hover:text-sky-300 hover:underline transition-colors"
                          >
                            {formatUserLabel(op.user_id, userNames)}
                          </button>
                          <p className="text-xs text-slate-500 font-mono">#{op.user_id}</p>
                        </td>
                        <td className="table-cell text-slate-300 text-sm whitespace-nowrap">
                          {TYPE_LABELS[op.type] ?? op.type}
                        </td>
                        <td
                          className={`table-cell text-right font-mono whitespace-nowrap ${directionClass(op.direction)}`}
                        >
                          {directionPrefix(op.direction)}
                          {formatCryptoAmount(op.amount)}{' '}
                          <span className="text-slate-500">{op.currency}</span>
                        </td>
                        <td className="table-cell text-right font-mono text-slate-400 whitespace-nowrap">
                          {formatCryptoAmount(op.balance_after)}
                        </td>
                        <td className="table-cell max-w-[280px]">
                          <p className="text-sm text-slate-300 truncate" title={op.description ?? ''}>
                            {op.description ?? '—'}
                          </p>
                          {op.reference_id && (
                            <p
                              className="text-xs text-slate-600 font-mono truncate"
                              title={op.reference_id}
                            >
                              {op.reference_id}
                            </p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {operations.last_page > 1 && (
                <div className="p-4 border-t border-slate-800 flex items-center justify-between gap-4">
                  <p className="text-xs text-slate-500">
                    {operations.total.toLocaleString('en-US')} operation
                    {operations.total === 1 ? '' : 's'}
                  </p>
                  <Pagination
                    currentPage={operations.current_page}
                    lastPage={operations.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <OperationDetailModal
        isOpen={selectedOperation !== null}
        onClose={() => setSelectedOperation(null)}
        operation={selectedOperation}
        userLabel={
          selectedOperation
            ? formatUserLabel(selectedOperation.user_id, userNames)
            : ''
        }
        showCategory={false}
        onViewUser={(userId) => {
          setSelectedOperation(null);
          setProfileUserId(userId);
        }}
      />

      <UserDetailModal
        isOpen={profileUserId !== null}
        onClose={() => setProfileUserId(null)}
        userId={profileUserId}
      />
    </Layout>
  );
};

export default ModuleOperationsPage;
